import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import DocIcon from "../../../../assets/images/segmentEditor/doc.svg";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import DocumentBlock from "../../Shared/DocumentBlock";
import ImageBlock from "../../Shared/ImageBlock";
import DocumentSettings from "./DocumentSettings";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";

const Container = styled.div`
  padding: ${(props) => props.padding};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const Document = ({
  documents,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  columns,
  showThumbnail,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const { document: documentProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  // Initialize with default props from reducer for new components
  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Document-specific properties
        if (props.documents === undefined) {
          props.documents = [...documentProps.documents];
        }
        
        if (props.margin === undefined) {
          props.margin = { ...documentProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = documentProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = documentProps.allSideMargin;
        }

        if (props.columns === undefined) {
          props.columns = documentProps.columns;
        }

        if (props.showThumbnail === undefined) {
          props.showThumbnail = documentProps.showThumbnail;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Document);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const renderDocuments = () => {
    if (!documents?.length) {
      return <ImageBlock style={{ borderRadius: 8 }} src={DocIcon} />;
    }

    return documents.map((doc) => (
      <DocumentBlock key={doc.id} doc={doc} />
    ));
  };

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <Container padding={marginStyle}>{renderDocuments()}</Container>
      </BlockIndicator>
    </div>
  );
};

export const DocumentDefaultProps = {
  documents: undefined,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  columns: undefined,
  showThumbnail: undefined,
};

Document.craft = {
  props: DocumentDefaultProps,
  related: {
    settings: DocumentSettings,
  },
};
